import { ed25519 } from "@noble/curves/ed25519";
import { hkdf } from "@noble/hashes/hkdf";
import { sha256 } from "@noble/hashes/sha256";
import { randomBytes } from "@noble/hashes/utils";
import { entropyToMnemonic, mnemonicToEntropy, validateMnemonic } from "@scure/bip39";
import { wordlist as french } from "@scure/bip39/wordlists/french";

/**
 * Compte éphémère local (dossier d'architecture, ch. 7).
 *
 * Une graine de 128 bits est tirée sur l'appareil, présentée sous forme de
 * 12 mots (BIP39, liste française — PO-10), et la clé Ed25519 en est dérivée.
 * Le serveur ne voit jamais la graine ni la clé privée, seulement la clé
 * publique, et seulement pendant une session.
 */

export const WORDLIST = french;

export interface Account {
  /** 12 mots séparés par des espaces. À noter par l'utilisateur. */
  mnemonic: string;
  /** Clé privée Ed25519, 32 octets. Ne quitte jamais l'appareil. */
  privateKey: Uint8Array;
  /** Clé publique Ed25519, hexadécimal minuscule sur 64 caractères. */
  publicKeyHex: string;
}

/**
 * L'étiquette de dérivation. **Elle ne suit pas le renommage en Hoa.**
 *
 * Elle entre dans le calcul qui transforme les douze mots en clé : la changer
 * donnerait une autre clé pour les mêmes douze mots. Tous les comptes déjà
 * créés cesseraient d'être reconnus, et les reçus déjà signés deviendraient
 * invérifiables — pour un mot que personne ne lit jamais. C'est un identifiant
 * de format, pas un nom de produit ; il ne change qu'avec une version (`v2`).
 */
const INFO = new TextEncoder().encode("gomgom/account/ed25519/v1");

export function toHex(b: Uint8Array): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

export function fromHex(s: string): Uint8Array {
  if (s.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(s)) throw new Error("hexadécimal invalide");
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = Number.parseInt(s.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** Nouvelle identité : 12 mots tirés au hasard sur l'appareil. */
export function createAccount(): Account {
  return accountFromEntropy(randomBytes(16));
}

export function accountFromEntropy(entropy: Uint8Array): Account {
  if (entropy.length !== 16) throw new Error("la graine doit faire 128 bits");
  const mnemonic = entropyToMnemonic(entropy, WORDLIST);
  return accountFromMnemonic(mnemonic);
}

/** Restauration : les mêmes 12 mots redonnent exactement la même identité. */
export function accountFromMnemonic(mnemonic: string): Account {
  const normalized = normalizeMnemonic(mnemonic);
  if (!validateMnemonic(normalized, WORDLIST)) throw new Error("phrase de 12 mots invalide");
  const entropy = mnemonicToEntropy(normalized, WORDLIST);
  const privateKey = hkdf(sha256, entropy, undefined, INFO, 32);
  return {
    mnemonic: normalized,
    privateKey,
    publicKeyHex: toHex(ed25519.getPublicKey(privateKey)),
  };
}

/** Tolère les espaces multiples, les majuscules et les retours à la ligne. */
export function normalizeMnemonic(mnemonic: string): string {
  return mnemonic.normalize("NFKD").trim().toLowerCase().split(/\s+/).join(" ");
}

export function isValidMnemonic(mnemonic: string): boolean {
  try {
    return validateMnemonic(normalizeMnemonic(mnemonic), WORDLIST);
  } catch {
    return false;
  }
}

/** Signe un défi du serveur : preuve de possession, sans identité. */
export function signChallenge(account: Account, nonceHex: string): string {
  return toHex(ed25519.sign(fromHex(nonceHex), account.privateKey));
}

export function verifyChallenge(publicKeyHex: string, nonceHex: string, signatureHex: string): boolean {
  try {
    return ed25519.verify(fromHex(signatureHex), fromHex(nonceHex), fromHex(publicKeyHex));
  } catch {
    return false;
  }
}

/**
 * Signe un message texte. Sert à prouver la possession d'une clé pour une
 * action qui n'est pas une entrée en session — créer une tribu, par exemple.
 */
export function signMessage(account: Account, message: string): string {
  return toHex(ed25519.sign(new TextEncoder().encode(message), account.privateKey));
}

export function verifyMessage(publicKeyHex: string, message: string, signatureHex: string): boolean {
  try {
    return ed25519.verify(fromHex(signatureHex), new TextEncoder().encode(message), fromHex(publicKeyHex));
  } catch {
    return false;
  }
}

/** Paire de clés du serveur, utilisée pour signer les reçus de session. */
export function createServerKeypair(): { privateKeyHex: string; publicKeyHex: string } {
  const sk = randomBytes(32);
  return { privateKeyHex: toHex(sk), publicKeyHex: toHex(ed25519.getPublicKey(sk)) };
}

export { randomBytes };
